import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'

// ── Types ────────────────────────────────────────────────────────────────────

type ToastVariant = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: string
  variant: ToastVariant
  message: string
  createdAt: number
}

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
  warning: (message: string) => void
}

// ── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

/**
 * useToast -- returns { success, error, info, warning } functions to show
 * toast notifications.  Must be used inside a <ToastProvider>.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast() must be used within a <ToastProvider>')
  }
  return ctx
}

// ── Constants ────────────────────────────────────────────────────────────────

const AUTO_DISMISS_MS = 4000
let toastCounter = 0

// ── Variant config ───────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<
  ToastVariant,
  { icon: typeof CheckCircle2; containerClass: string; iconClass: string; label: string }
> = {
  success: {
    icon: CheckCircle2,
    containerClass: 'bg-green-900/80 border-green-700',
    iconClass: 'text-green-400',
    label: 'Success',
  },
  error: {
    icon: AlertCircle,
    containerClass: 'bg-red-900/80 border-red-700',
    iconClass: 'text-red-400',
    label: 'Error',
  },
  info: {
    icon: Info,
    containerClass: 'bg-blue-900/80 border-blue-700',
    iconClass: 'text-blue-400',
    label: 'Information',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'bg-yellow-900/80 border-yellow-700',
    iconClass: 'text-yellow-400',
    label: 'Warning',
  },
}

// ── Single toast ─────────────────────────────────────────────────────────────

function ToastEntry({
  item,
  onDismiss,
}: {
  item: ToastItem
  onDismiss: (id: string) => void
}) {
  const config = VARIANT_CONFIG[item.variant]
  const Icon = config.icon

  // Auto-dismiss timer
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(item.id), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [item.id, onDismiss])

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={clsx(
        'flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm',
        'min-w-[300px] max-w-[420px]',
        'animate-slide-in-right',
        config.containerClass,
      )}
    >
      <Icon className={clsx('w-5 h-5 shrink-0 mt-0.5', config.iconClass)} aria-hidden="true" />
      <div className="flex-1">
        <p className="sr-only">{config.label}:</p>
        <p className="text-sm text-slate-100">{item.message}</p>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="shrink-0 p-0.5 rounded hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4 text-slate-300" aria-hidden="true" />
      </button>
    </div>
  )
}

// ── Provider ─────────────────────────────────────────────────────────────────

/**
 * ToastProvider -- wrap your app with this component.  Toast notifications
 * stack in the bottom-right corner and auto-dismiss after 4 seconds.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((variant: ToastVariant, message: string) => {
    const id = `toast-${++toastCounter}-${Date.now()}`
    setToasts((prev) => [...prev, { id, variant, message, createdAt: Date.now() }])
  }, [])

  const success = useCallback((msg: string) => push('success', msg), [push])
  const error = useCallback((msg: string) => push('error', msg), [push])
  const info = useCallback((msg: string) => push('info', msg), [push])
  const warning = useCallback((msg: string) => push('warning', msg), [push])

  return (
    <ToastContext.Provider value={{ success, error, info, warning }}>
      {children}

      {/* Toast container -- bottom-right, stacked */}
      {toasts.length > 0 && (
        <div
          aria-label="Notifications"
          className="fixed bottom-4 right-4 z-[9999] flex flex-col-reverse gap-2 pointer-events-auto"
        >
          {toasts.map((item) => (
            <ToastEntry key={item.id} item={item} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}
